/* Lookbook — Release / ADV Studio
   Temanın sepet altyapısını kullanır: routes.cart_add_url, fetchConfig,
   cart.getSectionsToRender(), cart.renderContents(), updateCartCounters(). */

if (!customElements.get('lookbook-section')) {
  class LookbookSection extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.init();
    }

    init() {
      const dataNode = this.querySelector('[data-lookbook-data]');
      if (!dataNode) return;

      try {
        this.data = JSON.parse(dataNode.textContent);
      } catch (e) {
        return;
      }
      if (!this.data.looks || !this.data.looks.length) return;

      this.strings = this.data.strings || {};
      this.modal = this.querySelector('[data-lookbook-modal]');
      this.dialog = this.querySelector('.lookbook__dialog');
      this.icons = {};

      const iconTpl = this.querySelector('[data-lookbook-icons]');
      if (iconTpl) {
        iconTpl.content.querySelectorAll('[data-icon]').forEach(node => {
          this.icons[node.dataset.icon] = node.innerHTML;
        });
      }

      this.formatter = new Intl.NumberFormat(this.data.locale || 'tr-TR', {
        style: 'currency',
        currency: this.data.currency || 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      this.current = -1;
      this.openRow = -1;
      this.warnRow = -1;
      this.slide = 0;
      this.picked = {};
      this.inCart = {};

      // Modalı body'ye taşı: temanın fade-in animasyonu section'a
      // stacking kökü oluşturuyor; içeride kalırsa fixed konum bozuluyor.
      this.modal.style.setProperty(
        '--lookbook-ratio',
        getComputedStyle(this).getPropertyValue('--lookbook-ratio').trim() || '3/4'
      );
      document.body.appendChild(this.modal);

      this.boundClick = this.onClick.bind(this);
      this.addEventListener('click', this.boundClick);
      this.modal.addEventListener('click', this.boundClick);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPopstate = this.onPopstate.bind(this);
      this.onResize = this.onResize.bind(this);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopstate);
      window.addEventListener('resize', this.onResize);

      this.openFromUrl();
    }

    get cart() {
      return (
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer')
      );
    }

    money(cents) {
      return this.formatter.format(cents / 100);
    }

    esc(value) {
      return String(value == null ? '' : value).replace(/[&<>"']/g, c => {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    stageRatio() {
      // Grid kartlarındaki oranın aynısı: section'daki --lookbook-ratio (örn. "53/79")
      const raw = getComputedStyle(this).getPropertyValue('--lookbook-ratio').trim();
      const parts = raw.split('/').map(parseFloat);
      if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) return parts[0] / parts[1];
      return 3 / 4;
    }

    syncLayout() {
      if (window.innerWidth <= 749) return; // mobil: merkezli pencere, CSS yönetiyor

      const header = document.querySelector('#header, .js-header, #shopify-section-header');
      let offset = 0;
      if (header) {
        const rect = header.getBoundingClientRect();
        // sticky header scroll ile kısmen gizlenebilir; sadece görünür kısmı say
        if (rect.height > 0) {
          offset = Math.min(Math.max(rect.bottom, 0), window.innerHeight * 0.4);
        }
      }

      const narrow = window.innerWidth <= 989;
      const gap = narrow ? 20 : 30;
      const maxW = window.innerWidth - (narrow ? 24 : 60);
      const availH = Math.max(window.innerHeight - offset - gap * 2, 320);
      const ratio = this.stageRatio();

      // Panel: okunabilir sabit aralık; kalan genişlik görsele
      const panelW = Math.min(Math.max(window.innerWidth * 0.36, 380), 500);

      let h = availH;
      let stageW = h * ratio;
      if (stageW + panelW > maxW) {
        stageW = Math.max(maxW - panelW, 280);
        h = stageW / ratio; // oran asla bozulmaz, popup küçülür
      }

      const top = offset + gap + Math.max(0, (availH - h) / 2);

      const s = this.modal.style;
      s.setProperty('--lookbook-header-offset', `${Math.round(offset)}px`);
      s.setProperty('--lookbook-top', `${Math.round(top)}px`);
      s.setProperty('--lookbook-h', `${Math.round(h)}px`);
      s.setProperty('--lookbook-stage-w', `${Math.round(stageW)}px`);
      s.setProperty('--lookbook-w', `${Math.round(stageW + panelW)}px`);
    }

    onResize() {
      if (this.current !== -1) this.syncLayout();
    }

    share() {
      const look = this.data.looks[this.current];
      const url = window.location.href;
      const payload = { title: `${look.title || 'Look ' + look.no} — ADV Studio`, url };

      if (navigator.share) {
        navigator.share(payload).catch(() => {});
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(url)
          .then(() => this.say('Bağlantı kopyalandı'))
          .catch(() => {});
      }
    }

    say(text) {
      const el = this.dialog.querySelector('[data-lookbook-msg]');
      if (el) el.textContent = text || '';
    }

    totals() {
      const look = this.data.looks[this.current];
      let sum = 0;
      let added = 0;
      look.products.forEach((p, i) => {
        sum += p.price;
        if (this.inCart[i] !== undefined) added += 1;
      });
      return { sum, added, count: look.products.length };
    }

    rowState(product, index) {
      if (this.inCart[index] !== undefined) {
        return `${this.icons.check || ''} ${this.esc(this.inCart[index])}`;
      }
      if (this.picked[index] !== undefined) {
        const v = product.variants.find(x => x.id === this.picked[index]);
        return v ? this.esc(v.label) : '';
      }
      if (!product.variants.some(v => v.available)) {
        return this.esc(this.strings.soldOut || '');
      }
      return '';
    }

    priceHTML(product) {
      let html = this.money(product.price);
      if (product.compareAt && product.compareAt > product.price) {
        html += `<span class="lookbook__was">${this.money(product.compareAt)}</span>`;
      }
      return html;
    }

    itemHTML(product, index) {
      const isOpen = this.openRow === index;
      const done = this.inCart[index] !== undefined;

      const sizes = product.variants
        .map(v => {
          return `<button type="button" class="lookbook__size" data-lookbook-size="${index}|${v.id}"
            aria-pressed="${this.picked[index] === v.id}"${v.available ? '' : ' disabled'}>${this.esc(v.label)}</button>`;
        })
        .join('');

      const addLabel = done
        ? `${this.strings.inCart || ''} · ${this.esc(this.inCart[index])}`
        : this.strings.addToCart || '';

      return `
        <div class="lookbook__item"${isOpen ? ' data-open' : ''}${this.warnRow === index ? ' data-warn' : ''}>
          <button type="button" class="lookbook__row" data-lookbook-row="${index}" aria-expanded="${isOpen}">
            ${
              product.image
                ? `<img class="lookbook__thumb" src="${this.esc(product.image)}" alt="" width="54" height="70" loading="lazy">`
                : '<span class="lookbook__thumb"></span>'
            }
            <span class="lookbook__rowtext">
              <span class="lookbook__name">${this.esc(product.title)}</span>
              <span class="lookbook__price">${this.priceHTML(product)}</span>
            </span>
            <span class="lookbook__state">${this.rowState(product, index)}</span>
            <span class="lookbook__chev" aria-hidden="true">${this.icons.chevron || ''}</span>
          </button>
          <div class="lookbook__body"><div class="lookbook__bodyinner"><div class="lookbook__bodypad">
            ${
              product.single
                ? ''
                : `<p class="lookbook__sizelabel">${this.esc(product.sizeName)}</p>
                   <div class="lookbook__sizes">${sizes}</div>`
            }
            <button type="button" class="button button--filled lookbook__add" data-lookbook-add="${index}"${done ? ' data-done' : ''}>${addLabel}</button>
            <a class="lookbook__link" href="${this.esc(product.url)}">${this.esc(this.strings.viewProduct || '')}</a>
          </div></div></div>
        </div>`;
    }

    ctaLabel() {
      const t = this.totals();
      if (t.added === 0) return `${this.strings.addLook || ''} · ${this.money(t.sum)}`;
      if (t.added < t.count) {
        return (this.strings.addRemaining || '').replace('[count]', t.count - t.added);
      }
      return this.strings.viewCart || '';
    }

    render() {
      const look = this.data.looks[this.current];
      const t = this.totals();
      const images = look.images || [];
      const src = images[this.slide] || images[0] || '';

      const dots =
        images.length > 1
          ? `<div class="lookbook__dots">${images
              .map(
                (_, i) =>
                  `<button type="button" class="lookbook__dot" data-lookbook-slide="${i}" aria-current="${i === this.slide}" aria-label="${i + 1}"></button>`
              )
              .join('')}</div>`
          : '';

      const shareIcon = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="M8 10V1.5M5.2 3.8L8 1l2.8 2.8"/><path d="M3 7.5v6h10v-6"/></svg>';

      this.dialog.innerHTML = `
        <div class="lookbook__stage">
          <button type="button" class="lookbook__stageclose" data-lookbook-close aria-label="${this.esc(this.strings.close || '')}">${this.icons.close || ''}</button>
          ${src ? `<img src="${this.esc(src)}" alt="${this.esc(look.title)}">` : ''}
          ${dots}
        </div>
        <div class="lookbook__panel">
          <div class="lookbook__panelhead">
            <div>
              <p class="lookbook__dlgno">Look ${this.esc(look.no)}${look.subtitle ? ` · ${this.esc(look.subtitle)}` : ''}</p>
              ${look.title ? `<h2 class="lookbook__dlgtitle">${this.esc(look.title)}</h2>` : ''}
            </div>
            <span class="lookbook__bag">${t.added ? `${t.added}/${t.count}` : ''}</span>
            <button type="button" class="lookbook__close" data-lookbook-share aria-label="Paylaş">${shareIcon}</button>
            <button type="button" class="lookbook__close" data-lookbook-close aria-label="${this.esc(this.strings.close || '')}">${this.icons.close || ''}</button>
          </div>
          ${look.note ? `<p class="lookbook__note">${this.esc(look.note)}</p>` : ''}
          ${look.products.map((p, i) => this.itemHTML(p, i)).join('')}
          <div class="lookbook__foot">
            <div class="lookbook__total">
              <span class="lookbook__totallabel">${this.esc(this.strings.lookTotal || '')}</span>
              <span class="lookbook__totalvalue">${this.money(t.sum)}</span>
            </div>
            <button type="button" class="button button--filled lookbook__cta" data-lookbook-cta>${this.ctaLabel()}</button>
            <p class="lookbook__msg" data-lookbook-msg role="status" aria-live="polite"></p>
            <div class="lookbook__nav">
              <button type="button" class="lookbook__navbtn" data-lookbook-step="-1"${this.current === 0 ? ' disabled' : ''}>
                <span aria-hidden="true">${this.icons.prev || ''}</span>${this.esc(this.strings.previous || '')}
              </button>
              <button type="button" class="lookbook__navbtn" data-lookbook-step="1"${this.current === this.data.looks.length - 1 ? ' disabled' : ''}>
                ${this.esc(this.strings.next || '')}<span aria-hidden="true">${this.icons.next || ''}</span>
              </button>
            </div>
          </div>
        </div>`;
    }

    open(index, push) {
      if (index < 0 || index >= this.data.looks.length) return;
      const fresh = this.current === -1;

      this.current = index;
      this.openRow = 0;
      this.warnRow = -1;
      this.slide = 0;
      this.picked = {};
      this.inCart = {};

      if (fresh) {
        this.lastFocus = document.activeElement;
        document.querySelector('body').classList.add('overflow-hidden', 'lookbook-open');
        this.syncLayout();
        this.modal.hidden = false;
      }

      this.render();
      this.dialog.focus();

      if (typeof trapFocus === 'function') trapFocus(this.dialog);

      if (push !== false) {
        const url = new URL(window.location.href);
        url.searchParams.set('look', this.data.looks[index].no);
        history[fresh ? 'pushState' : 'replaceState']({ look: index }, '', url);
      }
    }

    close(push) {
      if (this.current === -1) return;
      this.current = -1;
      this.modal.hidden = true;
      this.dialog.innerHTML = '';
      document.querySelector('body').classList.remove('overflow-hidden', 'lookbook-open');

      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.lastFocus);
      else if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();

      if (push !== false) {
        const url = new URL(window.location.href);
        url.searchParams.delete('look');
        history.pushState({ look: null }, '', url);
      }
    }

    step(delta) {
      const next = this.current + delta;
      if (next < 0 || next >= this.data.looks.length) return;
      this.open(next);
    }

    openFromUrl() {
      const no = new URL(window.location.href).searchParams.get('look');
      if (!no) return;
      const index = this.data.looks.findIndex(l => l.no === no);
      if (index > -1) this.open(index, false);
    }

    onPopstate() {
      const no = new URL(window.location.href).searchParams.get('look');
      if (!no) {
        this.close(false);
        return;
      }
      const index = this.data.looks.findIndex(l => l.no === no);
      if (index > -1) this.open(index, false);
      else this.close(false);
    }

    postItems(items, button, openDrawer) {
      const cart = this.cart;
      const config = fetchConfig('javascript');
      config.headers['Content-Type'] = 'application/json';
      config.headers['X-Requested-With'] = 'XMLHttpRequest';

      const payload = { items };

      if (cart) {
        payload.sections = cart.getSectionsToRender().map(section => section.section);
        payload.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }

      config.body = JSON.stringify(payload);
      if (button) button.setAttribute('aria-busy', 'true');

      return fetch(`${routes.cart_add_url}`, config)
        .then(response => response.json())
        .then(response => {
          if (response.status || response.errors) {
            throw new Error(response.description || response.message || this.strings.error);
          }
          if (cart) cart.renderContents(response, openDrawer === true);
          if (typeof updateCartCounters === 'function') updateCartCounters();
          if (typeof updateFreeShipping === 'function') updateFreeShipping();
          return response;
        })
        .finally(() => {
          if (button) button.removeAttribute('aria-busy');
        });
    }

    variantLabel(product, variantId) {
      const v = product.variants.find(x => x.id === variantId);
      return v ? v.label : '';
    }

    resolveVariant(product, index) {
      if (this.picked[index] !== undefined) return this.picked[index];
      if (product.variants.length === 1) return product.variants[0].id;
      return undefined;
    }

    addOne(index, button) {
      const product = this.data.looks[this.current].products[index];
      const variantId = this.resolveVariant(product, index);

      if (variantId === undefined) {
        this.warnRow = index;
        this.openRow = index;
        this.render();
        this.say(this.strings.selectSize);
        return;
      }

      this.postItems([{ id: variantId, quantity: 1 }], button, false)
        .then(() => {
          this.inCart[index] = this.variantLabel(product, variantId);
          this.warnRow = -1;

          let next = -1;
          this.data.looks[this.current].products.forEach((_, j) => {
            if (next === -1 && this.inCart[j] === undefined) next = j;
          });
          this.openRow = next;

          this.render();
          this.say(next === -1 ? this.strings.lookAdded : this.strings.added);
        })
        .catch(err => this.say(err.message));
    }

    addRemaining(button) {
      const look = this.data.looks[this.current];
      const items = [];
      let missing = -1;
      let missingCount = 0;

      look.products.forEach((product, i) => {
        if (this.inCart[i] !== undefined) return;
        const variantId = this.resolveVariant(product, i);
        if (variantId === undefined) {
          if (missing === -1) missing = i;
          missingCount += 1;
          return;
        }
        items.push({ id: variantId, quantity: 1, index: i });
      });

      if (missing !== -1) {
        this.warnRow = missing;
        this.openRow = missing;
        this.render();
        this.say((this.strings.missingSizes || '').replace('[count]', missingCount));
        return;
      }

      if (!items.length) return;

      this.postItems(
        items.map(x => ({ id: x.id, quantity: x.quantity })),
        button,
        true
      )
        .then(() => {
          items.forEach(x => {
            this.inCart[x.index] = this.variantLabel(look.products[x.index], x.id);
          });
          // Tüm look sepete girdi: popup kapanır, tema sepet çekmecesi öne çıkar
          this.close();
        })
        .catch(err => this.say(err.message));
    }

    /* ---------- olaylar ---------- */

    onClick(event) {
      const trigger = event.target.closest('[data-lookbook-open]');
      if (trigger) {
        this.open(parseInt(trigger.getAttribute('data-lookbook-open'), 10));
        return;
      }

      const scroller = event.target.closest('[data-lookbook-scroll]');
      if (scroller) {
        this.scrollTrack(parseInt(scroller.getAttribute('data-lookbook-scroll'), 10));
        return;
      }

      if (this.current === -1) return;

      if (event.target.closest('[data-lookbook-close]')) {
        this.close();
        return;
      }

      if (event.target.closest('[data-lookbook-share]')) {
        this.share();
        return;
      }

      const dot = event.target.closest('[data-lookbook-slide]');
      if (dot) {
        this.slide = parseInt(dot.getAttribute('data-lookbook-slide'), 10);
        this.render();
        return;
      }

      const row = event.target.closest('[data-lookbook-row]');
      if (row) {
        const i = parseInt(row.getAttribute('data-lookbook-row'), 10);
        this.openRow = this.openRow === i ? -1 : i;
        this.warnRow = -1;
        this.render();
        return;
      }

      const size = event.target.closest('[data-lookbook-size]');
      if (size) {
        const [i, id] = size.getAttribute('data-lookbook-size').split('|');
        this.picked[parseInt(i, 10)] = parseInt(id, 10);
        this.warnRow = -1;
        this.render();
        this.say('');
        return;
      }

      const add = event.target.closest('[data-lookbook-add]');
      if (add) {
        this.addOne(parseInt(add.getAttribute('data-lookbook-add'), 10), add);
        return;
      }

      const cta = event.target.closest('[data-lookbook-cta]');
      if (cta) {
        const t = this.totals();
        if (t.added === t.count) {
          const cart = this.cart;
          this.close();
          if (cart && typeof cart.open === 'function') cart.open();
          else window.location.href = routes.cart_url;
          return;
        }
        this.addRemaining(cta);
        return;
      }

      const step = event.target.closest('[data-lookbook-step]');
      if (step) this.step(parseInt(step.getAttribute('data-lookbook-step'), 10));
    }

    scrollTrack(direction) {
      const viewport = this.querySelector('[data-lookbook-viewport]');
      if (!viewport) return;
      const cell = viewport.querySelector('.lbcar__cell');
      if (!cell) return;
      const gap = parseFloat(getComputedStyle(cell.parentElement).columnGap) || 8;
      viewport.scrollBy({ left: direction * (cell.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }

    onKeydown(event) {
      if (this.current === -1) return;
      if (event.key === 'Escape') this.close();
      if (event.key === 'ArrowRight') this.step(1);
      if (event.key === 'ArrowLeft') this.step(-1);
    }
  }

  customElements.define('lookbook-section', LookbookSection);
}
